import { useClickAway } from "@uidotdev/usehooks";
import * as d3 from "d3";
import { useAtom } from "jotai";
import { DateTime } from "luxon";
import { type Ref, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type {
    EmissionRecordView,
    SiteNonDetect,
} from "../../../../../../apiClient/generated";
import {
    useResizeObserver,
    useZoomOnPlot,
} from "../../../../../../ui/Plot/hooks";
import { formatDateTime } from "../../../../../../utils/dates";
import { EMISSION_COLORS } from "../../../../../MapV2/constants";
import { hoverContextAtom, selectionContextAtom } from "../../../state";
import { computePlotTicks, TIMELINE_MARGINS } from "../plot";
import { EmissionPlotHover } from "./hover";
import { emissionsToMarks, type PlotEmissionMark } from "./marks";

const dsMap = {
    THIRD_PARTY: "thirdParty",
    SELF_REPORTED: "operatorProvided",
    EPA: "epa",
};

type Chart = d3.Selection<SVGGElement, unknown, null, undefined>;

export interface PlotPointsOptions {
    xScale: d3.ScaleTime<number, number>;
    yScale: d3.ScaleContinuousNumeric<number, number>;
    showIndividual: boolean;
    emphasisedEmissionId?: string;
    hoveredEmissionIds?: string[];
    onSelect: (record: EmissionRecordView) => void;
    onPick: (records: EmissionRecordView[], event: MouseEvent) => void;
    onHover: (event: MouseEvent, mark: PlotEmissionMark) => void;
    onHoverEnd: () => void;
}

type MarkGroup = d3.Selection<SVGGElement, PlotEmissionMark, null, undefined>;
type MarkOf<K extends PlotEmissionMark["kind"]> = Extract<
    PlotEmissionMark,
    { kind: K }
>;

// Draw order of mark kinds, lowest first.
const MARK_LAYER: Record<PlotEmissionMark["kind"], number> = {
    concentration: 0,
    dailyAverage: 1,
    point: 2,
    groupedEmissions: 3,
};

const recordColor = (record: EmissionRecordView) => {
    const color = EMISSION_COLORS[dsMap[record.dataPoint.dataSource]];
    return color ? `rgb(${color.join(",")})` : "#000";
};

const isHighlighted = (mark: PlotEmissionMark, options: PlotPointsOptions) =>
    mark.records.some(
        (r) =>
            r.id === options.emphasisedEmissionId ||
            options.hoveredEmissionIds?.includes(r.id),
    );

// Vertical error bar with caps, only visible while the mark is highlighted.
function drawErrorBar(
    el: MarkGroup,
    x: number,
    yLow: number,
    yHigh: number,
    visible: boolean,
): void {
    const bar = el
        .append("g")
        .attr("class", "error-bar")
        .attr("stroke", "black")
        .attr("stroke-width", 1)
        .style("opacity", visible ? 1 : 0)
        .style("pointer-events", "none");
    bar.append("line")
        .attr("x1", x)
        .attr("x2", x)
        .attr("y1", yHigh)
        .attr("y2", yLow);
    for (const y of [yLow, yHigh]) {
        bar.append("line")
            .attr("x1", x - 5)
            .attr("x2", x + 5)
            .attr("y1", y)
            .attr("y2", y);
    }
}

function drawDailyAverage(
    el: MarkGroup,
    mark: MarkOf<"dailyAverage">,
    options: PlotPointsOptions,
): void {
    const { xScale, yScale } = options;
    const x = xScale(mark.x);
    const width = Math.max(10, xScale(mark.xEnd) - x);
    const y = yScale(mark.y / 1000);
    const highlighted = isHighlighted(mark, options);

    const uncertainty = mark.record.dataPoint.detectedRateUncertainty;
    if (uncertainty != null) {
        drawErrorBar(
            el,
            x + width / 2,
            yScale((mark.y - uncertainty) / 1000),
            yScale((mark.y + uncertainty) / 1000),
            highlighted,
        );
    }

    el.append("rect")
        .attr("x", x)
        .attr("y", y - 5)
        .attr("width", width)
        .attr("height", 10)
        .attr("rx", 5)
        .attr("ry", 5)
        .attr("fill", recordColor(mark.record))
        .attr("stroke", highlighted ? "black" : "none")
        .attr("stroke-width", highlighted ? 2 : 0);
}

function drawPoint(
    el: MarkGroup,
    mark: MarkOf<"point">,
    options: PlotPointsOptions,
): void {
    const { xScale, yScale } = options;
    const x = xScale(mark.x);
    const highlighted = isHighlighted(mark, options);
    const emphasised = mark.id === options.emphasisedEmissionId;
    const { record } = mark;

    const uncertainty = record.dataPoint.detectedRateUncertainty;
    if (uncertainty != null) {
        drawErrorBar(
            el,
            x,
            yScale((mark.y - uncertainty) / 1000),
            yScale((mark.y + uncertainty) / 1000),
            highlighted,
        );
    }

    // Records not yet confirmed to a site are drawn hatched.
    const fill = record.site
        ? recordColor(record)
        : `url(#noInfrastructurePattern-${dsMap[record.dataPoint.dataSource]})`;
    let stroke = "transparent";
    if (highlighted) {
        stroke = "black";
    } else if (!record.site) {
        stroke = recordColor(record);
    }

    el.append("circle")
        .attr("cx", x)
        .attr("cy", yScale(mark.y / 1000))
        .attr("r", emphasised ? 7 : 5)
        .attr("fill", fill)
        .attr("stroke", stroke)
        .attr("stroke-width", highlighted ? 2 : record.site ? 0 : 1);
}

function drawGroup(
    el: MarkGroup,
    mark: MarkOf<"groupedEmissions">,
    options: PlotPointsOptions,
): void {
    const color = EMISSION_COLORS[dsMap[mark.records[0].dataPoint.dataSource]];
    el.append("circle")
        .attr("cx", options.xScale(mark.x))
        .attr("cy", options.yScale(mark.y / 1000))
        .attr("r", 5)
        .attr("stroke", "gray")
        .attr("stroke-width", 2)
        .attr("stroke-dasharray", "3,3")
        .attr("fill", color ? `rgba(${color.join(",")}, 0.7)` : "#000");
}

function drawConcentration(
    el: MarkGroup,
    mark: MarkOf<"concentration">,
    options: PlotPointsOptions,
): void {
    const x = options.xScale(mark.x);
    const [yBottom, yTop] = options.yScale.range();
    const emphasised = mark.id === options.emphasisedEmissionId;

    el.append("line")
        .attr("x1", x)
        .attr("x2", x)
        .attr("y1", yTop)
        .attr("y2", yBottom)
        .attr("stroke", recordColor(mark.record))
        .attr("stroke-width", emphasised ? 2.5 : 1.5)
        .attr("stroke-dasharray", mark.record.site ? "6,6" : "4,4")
        .style("opacity", 0.5);

    // Wider invisible line for easier hovering.
    el.append("line")
        .attr("x1", x)
        .attr("x2", x)
        .attr("y1", yTop)
        .attr("y2", yBottom)
        .attr("stroke", emphasised ? "#00000010" : "transparent")
        .attr("stroke-width", 10);
}

// Hatched fill for records not yet confirmed to a site, one per data source colour.
function addNoInfrastructurePatterns(chart: Chart): void {
    chart
        .append("defs")
        .selectAll("pattern")
        .data(Object.entries(EMISSION_COLORS))
        .join("pattern")
        .attr("id", ([key]) => `noInfrastructurePattern-${key}`)
        .attr("patternUnits", "userSpaceOnUse")
        .attr("width", 4)
        .attr("height", 4)
        .each(function ([, color]) {
            const pattern = d3.select(this);
            pattern
                .append("rect")
                .attr("width", 4)
                .attr("height", 4)
                .attr("fill", "white");
            pattern
                .append("path")
                .attr("d", "M-1,1 l2,-2 M0,4 l4,-4 M3,5 l2,-2")
                .attr("stroke", `rgb(${color.join(",")})`)
                .attr("stroke-width", 1);
        });
}

export function plotPoints(
    chart: Chart,
    marks: PlotEmissionMark[],
    options: PlotPointsOptions,
): void {
    addNoInfrastructurePatterns(chart);

    // Points already represented by a group are only shown on request.
    // Sorted so concentration lines render below and groups above everything.
    const visible = marks
        .filter(
            (m) => options.showIndividual || m.kind !== "point" || !m.inGroup,
        )
        .sort((a, b) => MARK_LAYER[a.kind] - MARK_LAYER[b.kind]);

    const groups = chart
        .append("g")
        .selectAll<SVGGElement, PlotEmissionMark>("g.mark")
        .data(visible, (m) => m.id)
        .join("g")
        .attr("class", "mark")
        .style("cursor", "pointer");

    groups.each(function (mark) {
        const el = d3.select<SVGGElement, PlotEmissionMark>(this);
        switch (mark.kind) {
            case "point":
                return drawPoint(el, mark, options);
            case "dailyAverage":
                return drawDailyAverage(el, mark, options);
            case "groupedEmissions":
                return drawGroup(el, mark, options);
            case "concentration":
                return drawConcentration(el, mark, options);
        }
    });

    // Shared listeners
    groups
        .on("click", (event: MouseEvent, mark) => {
            event.stopPropagation();
            if (mark.records.length > 1) {
                options.onPick(mark.records, event);
            } else {
                options.onSelect(mark.records[0]);
            }
        })
        .on("mouseover", (event: MouseEvent, mark) =>
            options.onHover(event, mark),
        )
        .on("mouseout", () => options.onHoverEnd());

    // Keep highlighted marks (and their error bars) on top; concentration
    // lines stay below so their hover area never covers other marks.
    groups
        .filter(
            (mark) =>
                mark.kind !== "concentration" && isHighlighted(mark, options),
        )
        .raise();
}

interface EmissionPlotProps {
    data: EmissionRecordView[];
    nonDetects: SiteNonDetect[];
    dataDateRange: {
        dateBefore: Date;
        dateAfter: Date;
    };
    logScale: boolean;
    showIndividualEmissionsFromGroups: boolean;
    setCursorPosition?: (data?: { x: Date; y: number }) => void;
}

export const EmissionPlot = ({
    data,
    nonDetects,
    dataDateRange,
    logScale,
    showIndividualEmissionsFromGroups,
    setCursorPosition,
}: EmissionPlotProps) => {
    const svgRef = useRef<SVGSVGElement>();
    const containerRef = useRef<HTMLDivElement>();
    const dimensions = useResizeObserver(containerRef);

    // Hover context handling with external state
    const [hoverData, setHoverData] = useState<{
        mark?: PlotEmissionMark;
        nonDetectId?: string;
    }>({});
    const [tooltipPosition, setTooltipPosition] = useState<{
        x: number;
        y: number;
    }>({ x: 0, y: 0 });
    // Global (page-wide) context hover
    const [sitePageHoverContext, setSitePageHoverContext] =
        useAtom(hoverContextAtom);

    // When clicking on multiple emissions, show dropdown
    const [pickerPosition, setPickerPosition] = useState<{
        x: number;
        y: number;
    }>();
    const [pickedEmissions, setPickedEmissions] = useState<
        EmissionRecordView[]
    >([]);

    // Selection context
    const [selectionContext, setSelectionContext] =
        useAtom(selectionContextAtom);

    // Follow the detail panel's arrows: the selected linked record
    // when there is one, the main record otherwise.
    const emphasisedEmissionId =
        selectionContext.focusedEmissionId ?? selectionContext.emissionId;

    // Close picker when clicking outside
    const pickerRef = useClickAway(() => {
        setPickerPosition(undefined);
        setPickedEmissions([]);
    });

    // Zoom controls
    const { zoomDomain, addZoomBrushHandle, resetZoom, currentTransform } =
        useZoomOnPlot({
            dimensions,
            svgRef,
            disableDoubleClickReset: true,
        });
    useEffect(() => {
        if (zoomDomain.x) {
            setSelectionContext((d) => ({
                ...d,
                dateRange: {
                    start: zoomDomain.x[0] as Date,
                    end: zoomDomain.x[1] as Date,
                },
            }));
        } else {
            setSelectionContext((d) => ({ ...d, dateRange: undefined }));
        }
    }, [zoomDomain, setSelectionContext]);

    const plotMarks = useMemo(() => {
        const marks = emissionsToMarks(data);
        return marks.filter(
            (m) =>
                m.xEnd >= dataDateRange.dateAfter &&
                m.x <= dataDateRange.dateBefore,
        );
    }, [data, dataDateRange]);

    useEffect(() => {
        const margin = {
            ...TIMELINE_MARGINS,
            bottom: 12,
            top: 1,
        };
        const width = dimensions.width - margin.left - margin.right;
        const height = dimensions.height - margin.top - margin.bottom;
        const containerRect = containerRef.current.getBoundingClientRect();

        // Clear previous SVG content
        d3.select(svgRef.current).selectAll("*").remove();

        // Create SVG
        const svg = d3
            .select(svgRef.current)
            .attr("viewBox", `0 0 ${dimensions.width} ${dimensions.height}`)
            .attr("width", dimensions.width)
            .attr("height", dimensions.height);

        // Create the chart group
        const chart = svg
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        /**
         * Y Scale functions and definition
         */
        const yScaleFn = (useLogScale = true) => {
            const scale = useLogScale ? d3.scaleLog() : d3.scaleLinear();

            // Collect all values that need to be considered for domain
            const allValues = [];

            // Add emission data points with uncertainty
            const validData = data.filter((d) => !!d.dataPoint.detectedRate);
            validData.forEach((d) => {
                const uncertainty =
                    (d.dataPoint.detectedRateUncertainty || 0) / 1000;
                const rate = d.dataPoint.detectedRate / 1000;
                allValues.push(rate - uncertainty);
                allValues.push(rate + uncertainty);
            });

            // Also consider grouped emissions
            plotMarks.forEach((m) => {
                if (m.kind === "groupedEmissions") {
                    allValues.push(m.y / 1000);
                }
            });

            // Add non-detect detection limits if enabled
            nonDetects.forEach((d) => {
                allValues.push(d.detectionLimit / 1000);
            });

            // Calculate domain
            let minValue = d3.min(allValues);
            let maxValue = d3.max(allValues);

            // For log scale, ensure minimum value is positive and add padding
            if (useLogScale) {
                minValue = Math.max(minValue || 0.001, 0.001);
                // Extend domain by a factor for log scale
                minValue = minValue / 1.1;
                maxValue = maxValue * 1.1;
            } else {
                minValue = Math.max(minValue || 0, 0);
                // Add padding for linear scale
                const padding = (maxValue - minValue) * 0.1;
                minValue = Math.max(0, minValue - padding);
                maxValue = maxValue + padding;
            }

            // Apply zoom domain if it exists
            if (zoomDomain.y) {
                if (useLogScale) {
                    // For log scale, ensure minimum is positive and respect zoom domain
                    minValue = Math.max(0.001, zoomDomain.y[0]);
                    maxValue = Math.max(zoomDomain.y[1], minValue * 1.1);
                } else {
                    // For linear scale, constrain to 0 baseline
                    minValue = 0;
                    maxValue = Math.max(zoomDomain.y[1], 0.1);
                }
            }

            return scale
                .domain([minValue, maxValue])
                .nice()
                .range([height - margin.bottom, margin.top]);
        };

        const yScaleDetection = yScaleFn(logScale);

        const computeYTicks = (numberOfTicks: number) => {
            const [start, end] = yScaleDetection.domain();
            if (logScale) {
                const interval = 10;
                const length = Math.log10(end) - Math.log10(start) + 1;
                return Array.from({ length }, (_, i) => start * interval ** i);
            } else {
                const interval = (end - start) / (numberOfTicks - 1);
                return Array.from(
                    { length: numberOfTicks },
                    (_, i) => start + interval * i,
                );
            }
        };

        const yAxisDetection = d3
            .axisLeft(yScaleDetection)
            .tickSizeOuter(0)
            .tickSize(5)
            .tickFormat((d) => d3.format(",")(d))
            .tickValues(computeYTicks(6));

        const yGrid = d3
            .axisLeft(yScaleDetection)
            .ticks(5, ",")
            .tickSize(-width)
            .tickFormat(() => "");

        /**
         * X Axis functions and definitions
         */
        // Date range
        const dateRange = [dataDateRange.dateAfter, dataDateRange.dateBefore];
        const xScale = d3.scaleUtc().domain(dateRange).range([0, width]);

        const { tickFormat, tickValues } = computePlotTicks({
            startDate: dataDateRange.dateAfter,
            endDate: dataDateRange.dateBefore,
            plotWidth: width,
        });

        // X Axis with guaranteed start/end ticks
        chart
            .append("g")
            .attr("class", "x-axis-full")
            .attr("transform", `translate(0,${height - margin.bottom})`)
            .call(
                d3
                    .axisBottom(xScale)
                    .tickValues(tickValues)
                    .tickSize(5)
                    .tickSizeOuter(0)
                    .tickPadding(2)
                    .tickFormat(tickFormat),
            )
            .selectAll("text")
            .attr("font-size", "12px")
            .attr("fill", "#777");

        // Add grid lines for x-axis
        chart
            .append("g")
            .attr("class", "grid")
            .attr("transform", `translate(0,${height - margin.bottom})`)
            .call(
                d3
                    .axisBottom(xScale)
                    .tickValues(tickValues)
                    .tickSize(-height + margin.top + margin.bottom)
                    .tickSizeOuter(0)
                    .tickFormat(() => ""),
            )
            .selectAll("line")
            .style("stroke", "#e5e7eb")
            .style("stroke-dasharray", "6,6");

        // Add grid lines for y-axis
        chart
            .append("g")
            .attr("class", "grid")
            .call(yGrid)
            .call((g) => g.select(".domain").remove())
            .selectAll("line")
            .style("stroke", "#e5e7eb")
            .style("stroke-width", "0.5")
            .style("stroke-dasharray", "6,6");

        chart
            .append("g")
            .classed("", true)
            .call(yAxisDetection)
            .selectAll("text")
            .style("font-size", "14px")
            .attr("text-anchor", "center")
            .attr("dx", -3)
            .attr("dy", (_d, i, arr) => {
                if (i === 0) {
                    return 0;
                } else if (i === arr.length - 1) {
                    return 12;
                }
                return 5;
            });

        // Add y-axis label
        svg.append("text")
            .classed("text-gray-500", true)
            .attr("transform", `rotate(-90) translate(${-height / 2}, 15)`)
            .attr("x", 0)
            .attr("y", 0)
            .attr("text-anchor", "middle")
            .style("font-size", "14px")
            .text("kg/hr (methane)");

        // Zoom handle
        addZoomBrushHandle({
            chart,
            height,
            width,
            xScale,
            yScale: yScaleDetection,
            constrainYToZero: false,
        });

        /**
         * Data
         */

        // Non-detects, drawn below the emission marks
        const nonDetectLines = chart
            .append("g")
            .selectAll("g")
            .data(
                nonDetects.filter(
                    (nd) =>
                        nd.timestamp >= dataDateRange.dateAfter &&
                        nd.timestamp <= dataDateRange.dateBefore,
                ),
            )
            .enter()
            .append("g");

        nonDetectLines
            .append("line")
            .attr("x1", (d) => xScale(d.timestamp))
            .attr("x2", (d) => xScale(d.timestamp))
            .attr("y1", margin.top)
            .attr("y2", (d) => {
                const detectionLimit = d.detectionLimit / 1000;
                const [minDomain, maxDomain] = yScaleDetection.domain();
                const clampedLimit = Math.min(
                    Math.max(detectionLimit, minDomain),
                    maxDomain,
                );
                return yScaleDetection(clampedLimit);
            })
            .attr("stroke", () => {
                return "#4096FF";
            })
            .attr("stroke-width", () => {
                return 7;
            })
            .style("opacity", 0.3)
            .on("mouseover", (event, d: SiteNonDetect) => {
                const [px, py] = d3.pointer(event, containerRef.current);
                const rect = containerRef.current.getBoundingClientRect();
                setTooltipPosition({ x: rect.left + px, y: rect.top + py });
                setHoverData({ nonDetectId: d.id });
            })
            .on("mouseout", () => {
                setHoverData({});
            });

        plotPoints(chart, plotMarks, {
            xScale,
            yScale: yScaleDetection,
            showIndividual: showIndividualEmissionsFromGroups,
            emphasisedEmissionId,
            hoveredEmissionIds: sitePageHoverContext?.emissionIds,
            onSelect: (record) => {
                const emissionTimestamp = DateTime.fromJSDate(
                    record.dataPoint.detectionTimestamp,
                );
                setSelectionContext((s) => ({
                    ...s,
                    emissionId: record.id,
                    dateRange: {
                        start: emissionTimestamp.minus({ days: 7 }).toJSDate(),
                        end: emissionTimestamp.plus({ days: 7 }).toJSDate(),
                    },
                    highlightPosition: {
                        date: emissionTimestamp.toJSDate(),
                        x: 0,
                    },
                }));
                setTimeout(() => setHoverData({}), 400);
            },
            onPick: (records, event) => {
                setPickedEmissions(records);
                setPickerPosition({ x: event.clientX, y: event.clientY });
                setTimeout(() => setHoverData({}), 400);
            },
            onHover: (event, mark) => {
                const [px, py] = d3.pointer(event, containerRef.current);
                const rect = containerRef.current.getBoundingClientRect();
                setTooltipPosition({ x: rect.left + px, y: rect.top + py });
                setHoverData({ mark });

                const emissionIds = mark.records.map((r) => r.id);
                const current = sitePageHoverContext?.emissionIds;
                const isSame =
                    emissionIds.length === current?.length &&
                    emissionIds.every((id, i) => id === current[i]);
                if (!isSame) {
                    setSitePageHoverContext({
                        emissionIds,
                        relatedPlumeImages: mark.records.map(
                            (r) => r.dataPoint.plumeImage,
                        ),
                        source: "page",
                    });
                }
            },
            onHoverEnd: () => {
                setHoverData({});
                setSitePageHoverContext(undefined);
            },
        });

        // Handle hover events
        if (setCursorPosition) {
            // Add a background rect to handle clicks outside of points/lines
            chart
                .on("mousemove", (event) => {
                    setCursorPosition({
                        x: xScale.invert(
                            event.clientX - containerRect.left - margin.left,
                        ),
                        y: yScaleDetection.invert(
                            event.clientY - containerRect.top - margin.top,
                        ),
                    });
                })
                .on("mouseout", () => {
                    setCursorPosition(undefined);
                });

            // Show highlight line if position is set
            if (selectionContext?.highlightPosition) {
                chart
                    .append("line")
                    .attr("class", "highlight-line")
                    .attr("x1", xScale(selectionContext.highlightPosition.date))
                    .attr("x2", xScale(selectionContext.highlightPosition.date))
                    .attr("y1", 0)
                    .attr("y2", height)
                    .attr("stroke", "#3b82f6")
                    .attr("stroke-width", 1)
                    .style("stroke-dasharray", "4,4")
                    .style("pointer-events", "none");
            }
        }
    }, [
        data,
        plotMarks,
        showIndividualEmissionsFromGroups,
        nonDetects,
        logScale,
        dimensions,
        dataDateRange,
        selectionContext,
        setSelectionContext,
        setCursorPosition,
        sitePageHoverContext,
        setSitePageHoverContext,
        addZoomBrushHandle,
        zoomDomain,
        emphasisedEmissionId,
    ]);

    return (
        <div ref={containerRef} className="w-full h-full relative">
            {(hoverData.mark || hoverData.nonDetectId) &&
                tooltipPosition &&
                createPortal(
                    <EmissionPlotHover
                        mark={hoverData.mark}
                        nonDetectId={hoverData.nonDetectId}
                        style={{
                            position: "fixed",
                            left: tooltipPosition.x,
                            top: tooltipPosition.y,
                            pointerEvents: "none",
                            transform:
                                "translate(-50%, -100%) translateY(-8px)",
                            zIndex: 9999,
                        }}
                    />,
                    document.body,
                )}
            <svg ref={svgRef} style={{ width: "100%", height: "100%" }} />
            {/* Reset zoom button - only show when zoomed or date range selected */}
            {(currentTransform.k !== 1 ||
                currentTransform.x !== 0 ||
                currentTransform.y !== 0 ||
                zoomDomain.x !== null ||
                zoomDomain.y !== null ||
                selectionContext?.dateRange) && (
                <button
                    type="button"
                    onClick={() => {
                        resetZoom();
                    }}
                    className="absolute top-2 right-2 px-3 py-1 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50 transition-colors text-sm"
                    title="Reset zoom"
                >
                    Reset Zoom
                </button>
            )}

            {pickedEmissions.length > 0 &&
                pickerPosition &&
                createPortal(
                    <div
                        ref={pickerRef as Ref<HTMLDivElement>}
                        className="fixed bg-white border border-gray-300 rounded-lg shadow-lg z-[10000] w-64"
                        style={{
                            left: pickerPosition.x,
                            top: pickerPosition.y,
                        }}
                    >
                        <div className="max-h-[200px] overflow-y-auto">
                            <div className="text-sm px-2 py-1 rounded-t-lg bg-ae-neutral-100">
                                Total emissions:{" "}
                                {(
                                    pickedEmissions.reduce(
                                        (sum, p) =>
                                            sum +
                                            (p.dataPoint.detectedRate || 0),
                                        0,
                                    ) / 1000
                                ).toFixed(2)}
                                {" kg/h"}
                            </div>
                            {pickedEmissions
                                .toSorted(
                                    (a, b) =>
                                        b.dataPoint.detectedRate -
                                        a.dataPoint.detectedRate,
                                )
                                .map((emission) => {
                                    const isSelected =
                                        emphasisedEmissionId === emission.id;
                                    const dataSource =
                                        dsMap[emission.dataPoint.dataSource];
                                    const color = EMISSION_COLORS[dataSource];
                                    const colorStyle = color
                                        ? `rgb(${color.join(",")})`
                                        : "#000";

                                    return (
                                        <button
                                            type="button"
                                            key={emission.id}
                                            onClick={() => {
                                                const emissionTimestamp =
                                                    DateTime.fromJSDate(
                                                        emission.dataPoint
                                                            .detectionTimestamp,
                                                    );
                                                setSelectionContext((s) => ({
                                                    ...s,
                                                    emissionId: emission.id,
                                                    dateRange: {
                                                        start: emissionTimestamp
                                                            .minus({ days: 7 })
                                                            .toJSDate(),
                                                        end: emissionTimestamp
                                                            .plus({ days: 7 })
                                                            .toJSDate(),
                                                    },
                                                    highlightPosition: {
                                                        date: emissionTimestamp.toJSDate(),
                                                        x: 0,
                                                    },
                                                }));
                                                setPickerPosition(undefined);
                                                setPickedEmissions([]);
                                            }}
                                            className={`w-full text-left px-2 py-1 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 ${
                                                isSelected ? "bg-blue-50" : ""
                                            }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <div
                                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                                    style={{
                                                        backgroundColor:
                                                            colorStyle,
                                                    }}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {emission.dataPoint
                                                            .detectedRate
                                                            ? `${(emission.dataPoint.detectedRate / 1000).toFixed(2)} kg/hr`
                                                            : "No rate"}
                                                    </div>
                                                    <div className="text-xs text-gray-500 truncate">
                                                        {formatDateTime(
                                                            emission.dataPoint
                                                                .detectionTimestamp,
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                        </div>
                    </div>,
                    document.body,
                )}
        </div>
    );
};
